import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Users as UsersIcon, Settings as SettingsIcon, LogOut, UserCog, Sparkles, Megaphone, Mail, Menu, X, Building2, Rocket, MapPin, ChevronDown, ShieldCheck, HelpCircle, Home, ListTodo, ClipboardList, BarChart3, Brain, Syringe, UserPlus, Bot } from "lucide-react";
import { useState, useEffect, Fragment } from "react";
import LocationSwitcher from "./LocationSwitcher";
import WhatsNewBell from "./WhatsNewBell";
import WhatsNewModal from "./WhatsNewModal";
import { fetchUnreadCount } from "@/lib/whatsNewApi";
import axios from "axios";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const API = process.env.REACT_APP_BACKEND_URL;

function Layout({ children, user, onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [needsSetup, setNeedsSetup] = useState(false);
  const [release, setRelease] = useState(null);
  useEffect(() => { axios.get(`${API}/api/rollout/app-version`).then((r) => setRelease(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (user?.role !== "tenant_admin") return;
    axios.get(`${API}/api/onboarding/state`).then(({ data }) => {
      setNeedsSetup(!data.gingr.connected);
      if (!data.gingr.connected && !sessionStorage.getItem("setupSeen") && window.location.pathname !== "/setup") {
        sessionStorage.setItem("setupSeen", "1");
        navigate("/setup");
      }
    }).catch(() => {});
  }, [user?.role, navigate]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Simple location state - prioritize localStorage (source of truth after location switch)
  const [currentLocation, setCurrentLocation] = useState(() => {
    // CRITICAL: Always check localStorage first - it's the source of truth after location switch
    const savedId = localStorage.getItem('currentLocationId');
    if (savedId && user && user.locations && user.locations.some(loc => loc.id === savedId)) {
      return savedId;
    }
    if (!user) return null;
    if (user.locations && user.locations.length > 0) {
      return user.locations[0].id;
    }
    return user.location_id || null;
  });

  // Simple location initialization - sync with localStorage
  useEffect(() => {
    if (user && user.locations && user.locations.length > 0) {
      const savedLocationId = localStorage.getItem('currentLocationId');
      const validSaved = savedLocationId && user.locations.some(loc => loc.id === savedLocationId);
      const locationId = validSaved ? savedLocationId : user.locations[0].id;
      
      setCurrentLocation(locationId);
      localStorage.setItem('currentLocationId', locationId);
      localStorage.setItem('selectedLocationId', locationId);
      // Belt-and-suspenders: set default header AND interceptor reads from localStorage
      axios.defaults.headers.common['X-Location-Id'] = locationId;
      
      const location = user.locations.find(loc => loc.id === locationId);
      if (location) {
        localStorage.setItem('selectedLocationName', location.name);
      }
    } else if (user && user.location_id) {
      setCurrentLocation(user.location_id);
      localStorage.setItem('currentLocationId', user.location_id);
      localStorage.setItem('selectedLocationId', user.location_id);
      axios.defaults.headers.common['X-Location-Id'] = user.location_id;
    }
  }, [user]);

  // Track unread release count for the Help dropdown red dot. Lives in Layout
  // (instead of inside the dropdown) so the dot also clears when the user opens
  // /whats-new from the bell — both react to the `whats-new-read` event.
  const [unreadReleases, setUnreadReleases] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const { unread } = await fetchUnreadCount();
        if (!cancelled) setUnreadReleases(unread);
      } catch { /* silent */ }
    };
    refresh();
    const handler = () => refresh();
    window.addEventListener("whats-new-read", handler);
    window.addEventListener("focus", handler);
    return () => {
      cancelled = true;
      window.removeEventListener("whats-new-read", handler);
      window.removeEventListener("focus", handler);
    };
  }, []);

  // Track pending signup-request count for the super-admin badge.
  const [pendingSignups, setPendingSignups] = useState(0);
  useEffect(() => {
    if (!user || user.role !== "super_admin") return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const { data } = await axios.get(`${API}/api/admin/signup-requests/count`);
        if (!cancelled) setPendingSignups(data.pending || 0);
      } catch { /* silent */ }
    };
    refresh();
    const handler = () => refresh();
    window.addEventListener("signup-requests-changed", handler);
    window.addEventListener("focus", handler);
    const interval = setInterval(refresh, 60000);
    return () => {
      cancelled = true;
      window.removeEventListener("signup-requests-changed", handler);
      window.removeEventListener("focus", handler);
      clearInterval(interval);
    };
  }, [user]);

  // Get current location name
  const getCurrentLocationName = () => {
    if (!user || !currentLocation) return "LeadGen Tracker";
    
    // Find location in user's locations array
    if (user.locations && user.locations.length > 0) {
      const location = user.locations.find(loc => loc.id === currentLocation);
      return location ? location.name : "LeadGen Tracker";
    }
    
    return "LeadGen Tracker";
  };

  const handleLocationChange = (locationId) => {
    // CRITICAL: Set localStorage and axios defaults FIRST
    localStorage.setItem('currentLocationId', locationId);
    localStorage.setItem('selectedLocationId', locationId);
    axios.defaults.headers.common['X-Location-Id'] = locationId;
    
    if (user && user.locations) {
      const location = user.locations.find(loc => loc.id === locationId);
      if (location) {
        localStorage.setItem('selectedLocationName', location.name);
      }
    }
    
    // Clear session storage and stale SWR caches to prevent showing wrong data
    sessionStorage.clear();
    
    // Update React state — key change forces children to remount and refetch
    setCurrentLocation(locationId);
  };

  // Main navigation items (always visible)
  const isPrivileged = user && (['admin', 'tenant_admin'].includes(user.role) || user.role === 'super_admin' || user.role === 'manager');
  const mainNavItems = [
    ...(user && ['admin', 'super_admin', 'tenant_admin'].includes(user.role) ? [{ path: "/exec-dashboard", label: "Exec Dash", icon: BarChart3 }] : []),
    { path: "/ops", label: "Daily Ops", icon: ClipboardList },
    { path: "/going-home", label: "Going Home", icon: Home },
    ...(isPrivileged ? [{ path: "/customer-intelligence", label: "Customer Intel", icon: Brain }] : []),
    ...(isPrivileged || user?.role === 'tenant_admin' ? [{ path: "/campaigns", label: "Campaigns", icon: Megaphone }] : []),
    { path: "/dashboard", label: "Leads Dashboard", icon: LayoutDashboard },
    { path: "/leads", label: "Leads", icon: UsersIcon, group: true },
    { path: "/follow-up-queue", label: "Follow-Up Queue", icon: ListTodo },
    { path: "/ai-insights", label: "AI Insights", icon: Sparkles, group: true },
    ...(user && ['admin', 'super_admin', 'tenant_admin'].includes(user.role) ? [{ path: "/agents", label: "Agents", icon: Bot }] : []),
    { path: "/contact-us", label: "Contact Us", icon: Mail },
  ];

  // Admin dropdown items
  const adminMenuItems = [];
  if (user && (['admin', 'tenant_admin'].includes(user.role) || user.role === 'super_admin')) {
    adminMenuItems.push({ path: "/admin/users", label: "Users", icon: UserCog });
    adminMenuItems.push({ path: "/settings", label: "Settings", icon: SettingsIcon });
    
    // Super Admin only items
    if (user.role === 'super_admin') {
      adminMenuItems.push({ path: "/admin/tenants", label: "Tenants", icon: ShieldCheck });
      adminMenuItems.push({ path: "/admin/rollout", label: "Rollout Center", icon: Rocket });
      adminMenuItems.push({ path: "/admin/signup-requests", label: "Signup Requests", icon: UserPlus, count: pendingSignups });
      adminMenuItems.push({ path: "/location-management", label: "Location Management", icon: Building2 });
      // NOTE: Legacy "Onboard Location" entry removed (2026-05-03).
      // It used /api/admin/onboard/location which created tenant-less locations,
      // breaking multi-tenant isolation. Use Tenants → Add Location instead.
    }
  }
  // Tenant-admin self-serve (Phase D). Also visible to admin & super_admin since
  // they may want to test the org-owner flow on their own tenant.
  if (user && ['tenant_admin', 'admin', 'super_admin'].includes(user.role) && user.tenant_id) {
    adminMenuItems.push({ path: "/settings/my-locations", label: "My Locations", icon: MapPin });
    adminMenuItems.push({ path: "/setup", label: "Setup", icon: Rocket });
  }

  // Help dropdown items
  const helpMenuItems = [];
  // What's New is visible to every authenticated user
  helpMenuItems.push({ path: "/whats-new", label: "What's New", icon: Sparkles, badge: "whats-new" });
  if (user && (['admin', 'tenant_admin'].includes(user.role) || user.role === 'super_admin')) {
    helpMenuItems.push({ path: "/getting-started", label: "Getting Started", icon: Rocket });
  }
  
  // Add Sales Pitch for Master Admin only (admin with no location_id)
  const isMasterAdmin = user && user.role === 'admin' && !user.location_id;
  if (isMasterAdmin) {
    helpMenuItems.push({ path: "/sales-pitch", label: "Sales Pitch", icon: Megaphone });
  }

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: '#F7FAFA' }}>
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        {/* Top Row: Branding + User Info */}
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <Link to="/ops" className="flex items-center gap-2 sm:gap-3 min-w-0 hover:opacity-80 transition-opacity">
              <img src="/fourpawiq-icon-gold.png" alt="FourPaw IQ" className="h-10 sm:h-12 flex-shrink-0" />
              <div className="border-l border-gray-300 pl-2 sm:pl-3 min-w-0">
                <h1 className="text-sm sm:text-base font-bold text-gray-900 truncate" style={{ fontFamily: 'Manrope' }}>
                  {getCurrentLocationName()}
                </h1>
                <span className="text-xs sm:text-sm font-semibold text-gray-700">CRM & Insights</span>
              </div>
            </Link>
            
            {/* Desktop: User controls */}
            <div className="hidden lg:flex items-center gap-3">
              <LocationSwitcher 
                user={user} 
                currentLocation={currentLocation}
                onLocationChange={handleLocationChange}
              />
              <WhatsNewBell />
              <div className="border-l border-gray-200 pl-3 flex items-center gap-3">
                <div className="text-right hidden xl:block">
                  <div className="text-sm font-medium text-gray-900">{user.email}</div>
                  <div className="text-xs text-gray-500 capitalize">{user.role.replace('_', ' ')}</div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onLogout}
                  data-testid="logout-button"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>

            {/* Mobile Hamburger */}
            <div className="lg:hidden flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Bottom Row: Desktop Navigation */}
        <div className="hidden lg:block border-t border-gray-100 bg-gray-50/50">
          <div className="w-full px-4 sm:px-6 lg:px-8">
            <nav className="flex items-center gap-1 h-10">
              {mainNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Fragment key={item.path}>
                    {item.group && <span className="mx-1.5 h-5 w-px bg-gray-300/70 shrink-0" aria-hidden="true" />}
                    <Link to={item.path}>
                      <Button
                        variant={isActive ? "default" : "ghost"}
                        size="sm"
                        className={`h-8 text-xs ${isActive ? "bg-[#1F8A7C] hover:bg-[#18705F] text-white" : ""}`}
                        data-testid={`nav-${item.label.toLowerCase()}`}
                      >
                        <Icon className="h-3.5 w-3.5 mr-1.5" />
                        {item.label}
                      </Button>
                    </Link>
                  </Fragment>
                );
              })}

              {/* Admin Dropdown */}
              {adminMenuItems.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="gap-1 h-8 text-xs relative">
                      <ShieldCheck className="h-3.5 w-3.5" />
                      Admin
                      <ChevronDown className="h-3 w-3" />
                      {pendingSignups > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white" data-testid="admin-signup-dot" />
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    {adminMenuItems.map((item, index) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;
                      return (
                        <div key={item.path}>
                          {index === 2 && <DropdownMenuSeparator />}
                          <DropdownMenuItem asChild>
                            <Link 
                              to={item.path}
                              className={`flex items-center cursor-pointer ${isActive ? 'bg-[#E6F6F3] text-[#1F8A7C] font-semibold' : ''}`}
                            >
                              <Icon className="h-4 w-4 mr-2" />
                              {item.label}
                              {item.count > 0 && (
                                <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full" data-testid="signup-count-badge">
                                  {item.count}
                                </span>
                              )}
                            </Link>
                          </DropdownMenuItem>
                        </div>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Help Dropdown */}
              {helpMenuItems.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="gap-1 h-8 text-xs relative">
                      <HelpCircle className="h-3.5 w-3.5" />
                      Help
                      <ChevronDown className="h-3 w-3" />
                      {unreadReleases > 0 && (
                        <span
                          className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white"
                          data-testid="help-unread-dot"
                        />
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    {helpMenuItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;
                      const showDot = item.badge === "whats-new" && unreadReleases > 0;
                      return (
                        <DropdownMenuItem key={item.path} asChild>
                          <Link 
                            to={item.path}
                            className={`flex items-center cursor-pointer ${isActive ? 'bg-[#E6F6F3] text-[#1F8A7C] font-semibold' : ''}`}
                          >
                            <Icon className="h-4 w-4 mr-2" />
                            <span className="flex-1">{item.label}</span>
                            {showDot && (
                              <span className="ml-2 h-2 w-2 rounded-full bg-red-500" />
                            )}
                          </Link>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </nav>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white">
            <nav className="flex flex-col py-2">
              {/* Main Navigation */}
              {mainNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link 
                    key={item.path} 
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button
                      variant="ghost"
                      className={`w-full justify-start px-6 py-3 ${isActive ? "bg-[#1F8A7C] hover:bg-[#18705F] text-white" : ""}`}
                      data-testid={`nav-${item.label.toLowerCase()}`}
                    >
                      <Icon className="h-5 w-5 mr-3" />
                      {item.label}
                    </Button>
                  </Link>
                );
              })}

              {/* Admin Menu Items */}
              {adminMenuItems.length > 0 && (
                <>
                  <div className="px-6 py-2 mt-2 border-t border-gray-200">
                    <div className="flex items-center text-xs font-semibold text-gray-500 uppercase mb-2">
                      <ShieldCheck className="h-4 w-4 mr-2" />
                      Admin
                    </div>
                  </div>
                  {adminMenuItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <Link 
                        key={item.path} 
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Button
                          variant="ghost"
                          className={`w-full justify-start px-6 py-3 ${isActive ? "bg-[#1F8A7C] hover:bg-[#18705F] text-white" : ""}`}
                        >
                          <Icon className="h-5 w-5 mr-3" />
                          {item.label}
                          {item.count > 0 && (
                            <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-bold text-white bg-red-500 rounded-full">
                              {item.count}
                            </span>
                          )}
                        </Button>
                      </Link>
                    );
                  })}
                </>
              )}

              {/* Help Menu Items */}
              {helpMenuItems.length > 0 && (
                <>
                  <div className="px-6 py-2 mt-2 border-t border-gray-200">
                    <div className="flex items-center text-xs font-semibold text-gray-500 uppercase mb-2">
                      <HelpCircle className="h-4 w-4 mr-2" />
                      Help
                    </div>
                  </div>
                  {helpMenuItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <Link 
                        key={item.path} 
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Button
                          variant="ghost"
                          className={`w-full justify-start px-6 py-3 ${isActive ? "bg-[#1F8A7C] hover:bg-[#18705F] text-white" : ""}`}
                        >
                          <Icon className="h-5 w-5 mr-3" />
                          {item.label}
                        </Button>
                      </Link>
                    );
                  })}
                </>
              )}

              {/* User Info & Logout */}
              <div className="border-t border-gray-200 mt-2 pt-2 px-6 py-2">
                <div className="mb-3">
                  <LocationSwitcher 
                    user={user} 
                    currentLocation={currentLocation}
                    onLocationChange={handleLocationChange}
                  />
                </div>
                <div className="text-sm font-medium text-gray-900">{user.email}</div>
                <div className="text-xs text-gray-500 capitalize mb-2">{user.role.replace('_', ' ')}</div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onLogout();
                  }}
                  className="w-full"
                  data-testid="logout-button-mobile"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content - key forces React to remount children on location switch */}
      <main className={["/ai-insights", "/admin/rollout"].includes(location.pathname) ? "max-w-[1920px] mx-auto px-3 sm:px-4 lg:px-6 py-4" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"} key={currentLocation}>
        {needsSetup && location.pathname !== "/setup" && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm flex items-center gap-3" data-testid="setup-banner">
            <span>Finish setup: connect Gingr so FourPawIQ can load your data.</span>
            <Link to="/setup" className="ml-auto font-semibold text-teal-700 hover:underline" data-testid="setup-banner-link">Connect Gingr</Link>
          </div>
        )}
        {children}
      </main>

      {/* First-login "What's New" modal — pops once per session when there's an unread release */}
      <WhatsNewModal />

      {/* Footer with Version & Timestamp */}
      <footer className="border-t border-gray-200 bg-white/50 backdrop-blur-sm mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-2 text-xs text-gray-500">
            <div className="flex items-center gap-4">
              <span className="font-medium" data-testid="app-version">FourPawIQ {release?.version || ""}</span>
              {release?.released_at && <>
                <span className="hidden sm:inline">•</span>
                <span className="text-gray-400" data-testid="app-released-at">Released {new Date(release.released_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
              </>}
            </div>
            <div className="text-gray-400">
              © {new Date().getFullYear()} Ariya Ventures Inc. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Layout;